import { useEffect, useRef } from "react";
import { useFormikContext } from "formik";

const isEmptyString = (value) => !String(value ?? "").trim();

const isAmountEmpty = (value) => {
  if (value === "" || value === null || value === undefined) {
    return true;
  }

  const normalized = String(value).replace(",", ".");
  const parsed = Number(normalized);
  return Number.isNaN(parsed);
};

const isCustomerNumberEmpty = (customerNumber, entities) => {
  const trimmedCustomerNumber = String(customerNumber ?? "").trim();
  if (!trimmedCustomerNumber) {
    return true;
  }

  return !(entities || []).some((entity) => entity.entityNumber === trimmedCustomerNumber);
};

export const focusInvoiceField = (container, fieldKey) => {
  if (!container || !fieldKey) {
    return false;
  }

  const fieldRoot =
    container.querySelector(`[data-invoice-field="${fieldKey}"]`) || container.querySelector(`[name="${fieldKey}"]`);

  if (!fieldRoot) {
    return false;
  }

  const focusable =
    fieldRoot.matches("input, textarea, select, button")
      ? fieldRoot
      : fieldRoot.querySelector('input:not([type="hidden"]):not([tabindex="-1"]), textarea, select, [role="combobox"]');

  if (!focusable || focusable.disabled) {
    return false;
  }

  focusable.focus();

  if (typeof focusable.select === "function" && focusable.type !== "date") {
    focusable.select();
  }

  return true;
};

export const focusFirstEmptyInvoiceField = (container, fieldDefinitions) => {
  if (!container || !Array.isArray(fieldDefinitions)) {
    return false;
  }

  for (const fieldDefinition of fieldDefinitions) {
    if (fieldDefinition.isDisabled?.()) {
      continue;
    }

    if (!fieldDefinition.isEmpty()) {
      continue;
    }

    return focusInvoiceField(container, fieldDefinition.key);
  }

  return false;
};

export const buildManualInvoiceFieldDefinitions = (detailDraft, entities) => [
  {
    key: "customerNumber",
    isEmpty: () => isCustomerNumberEmpty(detailDraft.customerNumber, entities),
  },
  {
    key: "invoiceNumber",
    isEmpty: () => isEmptyString(detailDraft.invoiceNumber),
  },
  {
    key: "invoiceDate",
    isEmpty: () => isEmptyString(detailDraft.invoiceDate),
  },
  {
    key: "countryCode",
    isEmpty: () => isEmptyString(detailDraft.countryCode),
  },
  {
    key: "supplierId",
    isEmpty: () => isEmptyString(detailDraft.supplierId),
    isDisabled: () => !detailDraft.countryCode,
  },
  {
    key: "claimProductTypeCode",
    isEmpty: () => isEmptyString(detailDraft.claimProductTypeCode) || isEmptyString(detailDraft.claimProductTypeId),
    isDisabled: () => !detailDraft.countryCode,
  },
  {
    key: "claimProductSubCode",
    isEmpty: () => isEmptyString(detailDraft.claimProductSubCode),
    isDisabled: () => !detailDraft.claimProductTypeCode,
  },
  {
    key: "invoiceGrossAmount",
    isEmpty: () => isAmountEmpty(detailDraft.invoiceGrossAmount),
  },
  {
    key: "invoiceVATAmountLocalCurrency",
    isEmpty: () => isAmountEmpty(detailDraft.invoiceVATAmountLocalCurrency),
  },
  {
    key: "currencyId",
    isEmpty: () => isEmptyString(detailDraft.currencyId),
  },
];

export const buildReviewOcrInvoiceFieldDefinitions = (values, entities, showExtraFields) => {
  const fieldDefinitions = [
    {
      key: "customerNumber",
      isEmpty: () => isCustomerNumberEmpty(values.customerNumber, entities),
    },
    {
      key: "invoiceNumber",
      isEmpty: () => isEmptyString(values.invoiceNumber),
    },
    {
      key: "invoiceDate",
      isEmpty: () => isEmptyString(values.invoiceDate),
    },
    {
      key: "countryCode",
      isEmpty: () => isEmptyString(values.countryCode),
    },
    {
      key: "supplierId",
      isEmpty: () => isEmptyString(values.supplierId),
      isDisabled: () => !values.countryCode,
    },
    {
      key: "claimProductTypeCode",
      isEmpty: () => isEmptyString(values.claimProductTypeCode),
      isDisabled: () => !values.countryCode,
    },
    {
      key: "claimProductSubCodeId",
      isEmpty: () => isEmptyString(values.claimProductSubCodeId),
      isDisabled: () => !values.claimProductTypeCode,
    },
  ];

  if (showExtraFields) {
    fieldDefinitions.push(
      {
        key: "languageCode",
        isEmpty: () => isEmptyString(values.languageCode),
      },
      {
        key: "freeText",
        isEmpty: () => isEmptyString(values.freeText),
      }
    );
  }

  fieldDefinitions.push(
    {
      key: "invoiceGrossAmount",
      isEmpty: () => isAmountEmpty(values.invoiceGrossAmount),
    },
    {
      key: "invoiceVATAmountLocalCurrency",
      isEmpty: () => isAmountEmpty(values.invoiceVATAmountLocalCurrency),
    },
    {
      key: "currencyId",
      isEmpty: () => isEmptyString(values.currencyId),
    }
  );

  return fieldDefinitions;
};

export const FocusFirstEmptyInvoiceFieldEffect = ({ containerRef, selectionKey, buildFieldDefinitions }) => {
  const { values } = useFormikContext();
  const pendingFocusRef = useRef(false);
  const previousSelectionKeyRef = useRef(null);

  useEffect(() => {
    if (!selectionKey) {
      previousSelectionKeyRef.current = null;
      return;
    }

    if (selectionKey !== previousSelectionKeyRef.current) {
      pendingFocusRef.current = true;
      previousSelectionKeyRef.current = selectionKey;
    }
  }, [selectionKey]);

  useEffect(() => {
    if (!pendingFocusRef.current || !selectionKey) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      pendingFocusRef.current = false;
      focusFirstEmptyInvoiceField(containerRef.current, buildFieldDefinitions(values));
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [selectionKey, values, buildFieldDefinitions, containerRef]);

  return null;
};
